import React, { useMemo } from 'react';
import _ from 'lodash';
import { LANGUAGES, DOC_COUNTS } from '../../constants';
import ExplanationCard from '../ExplanationCard';

const StatisticalAnalysisView = ({ selectedMethod, selectedModel, methodData, statisticalView }) => {
  const getComprehensiveStats = useMemo(() => {
    if (!selectedMethod || !selectedModel || !methodData[selectedMethod]?.[selectedModel]) {
      return {};
    }

    const stats = {};
    const modelData = methodData[selectedMethod][selectedModel];

    LANGUAGES.forEach(lang => {
      const values = modelData[lang] || [];
      const validValues = values.filter(val => val !== null && val !== undefined && !isNaN(val));
      
      if (validValues.length > 0) {
        stats[lang] = {
          values: validValues,
          mean: _.mean(validValues),
          max: _.max(validValues),
          min: _.min(validValues),
          std: validValues.length > 1 ? Math.sqrt(_.sum(validValues.map(v => Math.pow(v - _.mean(validValues), 2))) / (validValues.length - 1)) : 0,
          trend: validValues.length > 1 ? validValues[validValues.length - 1] - validValues[0] : 0
        };
      }
    });

    return stats;
  }, [selectedMethod, selectedModel, methodData]);

  const getOptimalDocCounts = useMemo(() => {
    if (!selectedMethod || !selectedModel || !methodData[selectedMethod]?.[selectedModel]) {
      return {};
    }

    const optimal = {};
    const modelData = methodData[selectedMethod][selectedModel];

    LANGUAGES.forEach(lang => {
      const values = modelData[lang] || [];
      if (values.length > 0) {
        const maxValue = _.max(values);
        const maxIndex = values.indexOf(maxValue);
        optimal[lang] = {
          docCount: DOC_COUNTS[maxIndex],
          score: maxValue,
          improvement: maxValue - (values[0] || 0)
        };
      }
    });

    return optimal;
  }, [selectedMethod, selectedModel, methodData]);

  if (!selectedMethod || !selectedModel || !getComprehensiveStats[Object.keys(getComprehensiveStats)[0]]) {
    return (
      <div className="mb-8">
        <h3 className="text-3xl font-bold text-gray-800 mb-3 text-center">
          Detailed Statistical Analysis
        </h3>
        <p className="text-gray-600 text-center py-8">Select a method and model to view statistical analysis.</p>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <ExplanationCard title="Statistical Analysis Explanation">
        <p><strong>Mean:</strong> Average performance score across all document counts.</p>
        <p><strong>Max/Min:</strong> Highest and lowest performance scores observed.</p>
        <p><strong>Standard Deviation:</strong> Measure of variability in performance (lower = more consistent).</p>
        <p><strong>Trend:</strong> Performance change from first to last document count (positive = improving, negative = declining).</p>
        <p><strong>Optimal Doc Count:</strong> Document count that achieved the maximum performance score.</p>
        <p><strong>Improvement:</strong> Performance gain from optimal document count compared to baseline (0 documents).</p>
      </ExplanationCard>

      <h3 className="text-3xl font-bold text-gray-800 mb-3 text-center">
        Detailed Statistical Analysis
      </h3>
      <p className="text-lg text-gray-600 text-center mb-8">
        Comprehensive statistical breakdown for {selectedMethod} method - {selectedModel} model.
      </p>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Statistical Summary Table */}
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <h4 className="text-xl font-semibold text-gray-800 mb-4">Statistical Summary</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Language</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mean</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Std Dev</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trend</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(getComprehensiveStats).map(([lang, stats]) => (
                  <tr key={lang} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{lang}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{stats.mean.toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{stats.max.toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{stats.min.toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{stats.std.toFixed(2)}</td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${stats.trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {stats.trend >= 0 ? '+' : ''}{stats.trend.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Optimal Document Counts */}
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <h4 className="text-xl font-semibold text-gray-800 mb-4">Optimal Document Counts</h4>
          <div className="space-y-4">
            {Object.entries(getOptimalDocCounts).map(([lang, optimal]) => (
              <div key={lang} className="border-l-4 border-blue-500 pl-4 py-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-900">{lang}</span>
                  <span className="text-sm text-gray-600">Score: {optimal.score.toFixed(2)}</span>
                </div>
                <div className="text-sm text-gray-600">
                  Optimal: {optimal.docCount} documents 
                  <span className={`ml-2 font-medium ${optimal.improvement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ({optimal.improvement >= 0 ? '+' : ''}{optimal.improvement.toFixed(2)} improvement)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticalAnalysisView;